import { useMemo, useEffect } from "react";
import { useLocation } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "~/app/store";
import { useFlag } from "~/config/flags";
import { useClientConfig } from "~/hooks/useClientConfig";
import {
  useToteSubscription,
  usePickSubscription,
  usePTLSubscription,
  useGridV2Subscription,
  useHeartbeatSubscription
} from "~/lib/signalr";
import { selectThisWorkstation } from "~/redux/selectors/workstationsSelectors";
import { useLazyGetAndonStateQuery } from "~/redux/warehouse/andon.hooks";
import { useGetAutostoreGridsQuery } from "~/redux/warehouse/autostoreGrid.hooks";
import { useGetWorkstationsQuery } from "~/redux/warehouse/workstation.hooks";
import {
  ToteEventDto,
  SignalRPickEventDto,
  PTLEventDto,
  AutostoreEvent,
  HeartbeatEvent
} from "~/types/api";

import {
  portIdFromEvent,
  gridIdFromEvent,
  binIdFromEvent,
  workstationIdFromEvent
} from "./andon.logic";

import {
  setHandRaised,
  setPortStatus,
  setPortIsOpen,
  setPortHasBins,
  setGridSystemMode,
  selectAndonEventSaveLimit,
  addEventToHistory
} from "./andon.slice";
import { AndonStatus } from "./andon.types";
import {
  handleHeartbeatPing,
  handleHeartbeatPong
} from "./heartbeat/heartbeatHandlers";

export function EventListeningComponent() {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { extendPongData } = useFlag();
  const { an_andonFeaturesEnabled } = useClientConfig();
  const siteWorkstation = useAppSelector(selectThisWorkstation);
  const authMethod = useAppSelector((state) => state.login.authMethod);
  const binsMap = useAppSelector((state) => state.andon.portHasBinsById);
  const eventSaveLimit = useAppSelector(selectAndonEventSaveLimit);
  const userFc = useAppSelector((state) => state.store.usersFulfillmentCenter);

  const { data: autostoreGrids } = useGetAutostoreGridsQuery(undefined, {
    skip: !userFc
  });

  const [triggerGetAndon] = useLazyGetAndonStateQuery();

  useEffect(() => {
    if (!autostoreGrids) return;
    autostoreGrids.forEach((grid) => {
      void triggerGetAndon({ autostoreGridId: grid.autostoreGridId });
    });
  }, [autostoreGrids, triggerGetAndon]);

  const { data: workstations } = useGetWorkstationsQuery(undefined, {
    skip: !userFc
  });

  const portToWorkstationIdMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const ws of workstations ?? []) {
      for (const port of ws.ports) {
        map[String(port.portId)] = ws.id;
        if (port.parentPortId != null) {
          map[String(port.parentPortId)] = ws.id;
        }
      }
    }
    return map;
  }, [workstations]);
  const isOnServiceSupport = location.pathname.includes("service-support");
  const shouldListenToExpandedEvents =
    an_andonFeaturesEnabled && (authMethod === "ANDON" || isOnServiceSupport);

  const gridSubscription = (autostoreEvent: AutostoreEvent) => {
    if (!an_andonFeaturesEnabled) return;

    switch (autostoreEvent.case) {
      case "PortMode": {
        const portId = autostoreEvent.event.portId.toString();

        dispatch(
          setPortIsOpen({
            portId,
            isOpen: autostoreEvent.event.portMode === "Open"
          })
        );

        if (eventSaveLimit > 0) {
          dispatch(
            addEventToHistory({
              hub: "grid",
              portId,
              gridId: autostoreEvent.event.gridId,
              event: autostoreEvent,
              timestamp: new Date(),
              workstationId: portToWorkstationIdMap[portId]
            })
          );
        }

        return;
      }

      case "PortError": {
        const portId = autostoreEvent.event.portId.toString();
        const status: AndonStatus = autostoreEvent.event.hasError
          ? "bad"
          : "good";

        dispatch(setPortStatus({ portId, status }));

        if (eventSaveLimit > 0) {
          dispatch(
            addEventToHistory({
              hub: "grid",
              portId,
              gridId: autostoreEvent.event.gridId,
              status,
              event: autostoreEvent,
              timestamp: new Date(),
              workstationId:
                workstationIdFromEvent(autostoreEvent) ||
                portToWorkstationIdMap[portId]
            })
          );
        }

        return;
      }

      case "HandToggled": {
        dispatch(
          setHandRaised({
            workstationId: autostoreEvent.event.workstationId,
            handRaised: autostoreEvent.event.raiseHand,
            // we should put the timestamp on the event perhaps?
            timestamp: autostoreEvent.event.raiseHand
              ? new Date().toISOString()
              : undefined
          })
        );

        if (eventSaveLimit > 0) {
          dispatch(
            addEventToHistory({
              hub: "grid",
              gridId: gridIdFromEvent(autostoreEvent),
              event: autostoreEvent,
              timestamp: new Date(),
              workstationId: autostoreEvent.event.workstationId
            })
          );
        }
        return;
      }

      case "SystemModeChanged": {
        dispatch(
          setGridSystemMode({
            gridId: autostoreEvent.event.gridId,
            systemMode: autostoreEvent.event.systemMode
          })
        );

        if (eventSaveLimit > 0) {
          dispatch(
            addEventToHistory({
              hub: "grid",
              gridId: autostoreEvent.event.gridId,
              event: autostoreEvent,
              timestamp: new Date()
            })
          );
        }
        return;
      }

      case "BinModeChange": {
        const { binMode, portId, binId } = autostoreEvent.event;
        if (!portId) return;

        const portIdStr = portId.toString();

        dispatch(
          setPortIsOpen({
            portId: portIdStr,
            isOpen: binMode === "O"
          })
        );

        const existingBins = binsMap[portIdStr] || [];
        const newBins =
          binMode === "O"
            ? Array.from(new Set([...existingBins, binId]))
            : existingBins.filter((b) => b !== binId);

        dispatch(setPortHasBins({ portId: portIdStr, bins: newBins }));

        if (eventSaveLimit > 0) {
          dispatch(
            addEventToHistory({
              hub: "grid",
              gridId: autostoreEvent.event.gridId,
              portId: portIdStr,
              binId: autostoreEvent.event.binId.toString(),
              event: autostoreEvent,
              timestamp: new Date()
            })
          );
        }

        return;
      }
      default: {
        if (!shouldListenToExpandedEvents) return;
        const portId = portIdFromEvent(autostoreEvent);
        const workstationId = workstationIdFromEvent(autostoreEvent);

        if (eventSaveLimit > 0) {
          dispatch(
            addEventToHistory({
              hub: "grid",
              portId,
              gridId: gridIdFromEvent(autostoreEvent),
              binId: binIdFromEvent(autostoreEvent),
              event: autostoreEvent,
              timestamp: new Date(),
              workstationId:
                workstationId ||
                (portId ? portToWorkstationIdMap[portId] : undefined)
            })
          );
        }
        return;
      }
    }
  };
  useGridV2Subscription(gridSubscription);

  const pickSubscription = (autostoreEvent: SignalRPickEventDto) => {
    if (!shouldListenToExpandedEvents) return;

    const portId = portIdFromEvent(autostoreEvent);

    if (eventSaveLimit > 0) {
      dispatch(
        addEventToHistory({
          hub: "pick",
          portId: portIdFromEvent(autostoreEvent),
          binId: binIdFromEvent(autostoreEvent),
          event: autostoreEvent,
          timestamp: new Date(),
          workstationId:
            workstationIdFromEvent(autostoreEvent) ||
            (portId ? portToWorkstationIdMap[portId] : undefined)
        })
      );
    }
  };

  usePickSubscription(pickSubscription);

  const toteSubscription = (autostoreEvent: ToteEventDto) => {
    if (!shouldListenToExpandedEvents) return;

    const portId = portIdFromEvent(autostoreEvent);

    if (eventSaveLimit > 0) {
      dispatch(
        addEventToHistory({
          hub: "tote",
          portId: portIdFromEvent(autostoreEvent),
          gridId: gridIdFromEvent(autostoreEvent),
          event: autostoreEvent,
          timestamp: new Date(),
          workstationId:
            workstationIdFromEvent(autostoreEvent) ||
            (portId ? portToWorkstationIdMap[portId] : undefined)
        })
      );
    }
  };

  useToteSubscription(toteSubscription);

  const ptlSubscription = (autostoreEvent: PTLEventDto) => {
    if (!shouldListenToExpandedEvents) return;

    const portId = portIdFromEvent(autostoreEvent);

    if (eventSaveLimit > 0) {
      dispatch(
        addEventToHistory({
          hub: "ptl",
          portId: portIdFromEvent(autostoreEvent),
          gridId: gridIdFromEvent(autostoreEvent),
          event: autostoreEvent,
          timestamp: new Date(),
          workstationId:
            workstationIdFromEvent(autostoreEvent) ||
            (portId ? portToWorkstationIdMap[portId] : undefined)
        })
      );
    }
  };
  usePTLSubscription(ptlSubscription);

  const heartbeatSubscription = (data: HeartbeatEvent) => {
    if (!siteWorkstation || !an_andonFeaturesEnabled) return;

    switch (data.case) {
      case "Ping":
        void (async () => {
          await handleHeartbeatPing({
            data,
            siteWorkstationId: siteWorkstation.id,
            extendPongData
          });
        })();
        break;

      case "Pong":
        handleHeartbeatPong({
          data,
          dispatch
        });
        break;
    }
  };
  useHeartbeatSubscription(heartbeatSubscription);

  return null;
}

export default EventListeningComponent;
